import Modal from "@/components/atoms/Modal";
import Select, { SelectPaginateAsync } from "@/components/atoms/Select";
import useLoadOptions from "@/hooks/useLoadOptions";
import {
  INVALID_FORM_MESSAGE,
  INVALID_MINIMUM_CARACTERE_MESSAGE,
} from "@/constants/validatorMessages";
import {
  EMAIL_TEMPLATE_CONDITIONS,
  EmailTemplateForm,
} from "@/interfaces/EmailTemplate";
import { USER_ROLES, UserRoleType } from "@/interfaces/IUser";
import { IModalMethods } from "@/interfaces/Modal";
import {
  useGetEmailTemplateQuery,
  useStoreEmailTemplateMutation,
  useUpdateEmailTemplateMutation,
} from "@/services/emailTemplates";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Col, Form, Row } from "react-bootstrap";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { ToastContentProps, toast } from "react-toastify";

interface StoreEmailTemplateModalMethods extends IModalMethods {
  showOrHiddenModal: (id?: string) => void;
}

const StoreEmailTemplateModal: ForwardRefRenderFunction<
  StoreEmailTemplateModalMethods
> = (_, ref) => {
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  const [editingId, setEditingId] = useState<string | undefined>(undefined);

  const {
    control,
    handleSubmit,
    register,
    reset,
    setValue,
    formState: { errors },
  } = useForm<EmailTemplateForm>();

  const [storeEmailTemplateAsync] = useStoreEmailTemplateMutation();
  const [updateEmailTemplateAsync] = useUpdateEmailTemplateMutation();
  const { data: emailTemplate } = useGetEmailTemplateQuery(editingId || "", {
    skip: !editingId,
  });
  const { loadOrganisationsOptions } = useLoadOptions();

  useEffect(() => {
    if (emailTemplate && editingId) {
      setValue("templateId", emailTemplate.templateId);
      setValue("label", emailTemplate.label);
      setValue(
        "roles",
        emailTemplate.roleEmailTemplates.map((roleTemplate) => ({
          value: roleTemplate.role,
          label:
            USER_ROLES.find((r) => r.value === roleTemplate.role)?.label ||
            roleTemplate.role,
        }))
      );
      const foundCondition = EMAIL_TEMPLATE_CONDITIONS.find(
        (c) => c.value === emailTemplate.condition
      );
      if (foundCondition) {
        setValue("condition", foundCondition);
      }
      setValue("organisation", emailTemplate.organisation ?? null);
    }
  }, [emailTemplate, editingId, setValue]);

  const showOrHiddenModal = (id?: string) => {
    if (id) {
      setEditingId(id);
    } else {
      setEditingId(undefined);
      reset();
    }
    ModalRef.current?.showOrHiddenModal();
  };

  useImperativeHandle(ref, () => ({
    showOrHiddenModal,
  }));

  const onSubmit: SubmitHandler<EmailTemplateForm> = ({
    roles,
    condition,
    organisation,
    ...data
  }) => {
    const payload = {
      ...data,
      roles: roles.map((role) => role.value as UserRoleType),
      condition: condition ? (condition.value as string) : undefined,
      organisationId: organisation?.id ?? null,
    };

    const mutation = editingId
      ? updateEmailTemplateAsync([editingId, payload])
      : storeEmailTemplateAsync(payload);

    toast.promise(mutation.unwrap(), {
      pending: editingId
        ? "Modification du template en cours..."
        : "Ajout du template en cours...",
      success: {
        render() {
          reset();
          showOrHiddenModal();
          return (
            <p style={{ marginBottom: 0, textAlign: "center" }}>
              {editingId
                ? "Le template a bien été modifié 🤩"
                : "Le template a bien été ajouté 🤩"}
            </p>
          );
        },
      },
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          if (data?.status === 422) {
            return "Les champs que vous avez remplis semblent être incorrects.";
          }

          return "Une erreur est survenue";
        },
      },
    });
  };

  const onError: SubmitErrorHandler<EmailTemplateForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <Modal
      ref={ModalRef}
      title={editingId ? "Modifier un template" : "Ajouter un template"}
      requiredLabel
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      <Row className="gx-3">
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Template ID <span className="text-danger">*</span> :
            </Form.Label>
            <Form.Control
              placeholder="ex : welcome-email"
              type="text"
              disabled={!!editingId}
              {...register("templateId", {
                required: "Le template ID est requis",
                minLength: {
                  value: 2,
                  message: INVALID_MINIMUM_CARACTERE_MESSAGE,
                },
              })}
              isInvalid={!!errors.templateId}
            />
            <Form.Control.Feedback type="invalid">
              {errors.templateId?.message}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Identifiant unique du template (ex: welcome-email,
              invoice-reminder)
            </Form.Text>
          </Form.Group>
        </Col>

        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Label <span className="text-danger">*</span> :
            </Form.Label>
            <Form.Control
              placeholder="ex : Email de bienvenue"
              type="text"
              {...register("label", {
                required: "Le label est requis",
                minLength: {
                  value: 2,
                  message: INVALID_MINIMUM_CARACTERE_MESSAGE,
                },
              })}
              isInvalid={!!errors.label}
            />
            <Form.Control.Feedback type="invalid">
              {errors.label?.message}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Nom descriptif du template
            </Form.Text>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Rôles autorisés <span className="text-danger">*</span> :
            </Form.Label>
            <Controller
              control={control}
              name="roles"
              rules={{
                required: "Veuillez sélectionner au moins un rôle",
              }}
              render={({ field }) => (
                <Select
                  options={USER_ROLES}
                  value={field.value}
                  isMulti
                  onChange={field.onChange}
                  placeholder="Sélectionnez les rôles..."
                />
              )}
            />
            {!!errors.roles ? (
              <h6 className="mb-0 mt-0 mt-1 text-danger">
                {errors.roles?.message}
              </h6>
            ) : null}
            <Form.Text className="text-muted">
              Sélectionnez les rôles qui auront accès à ce template
            </Form.Text>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>Condition :</Form.Label>
            <Controller
              control={control}
              name="condition"
              rules={{
                required: "Veuillez sélectionner une condition",
              }}
              render={({ field }) => (
                <Select
                  options={EMAIL_TEMPLATE_CONDITIONS}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Sélectionnez une condition..."
                />
              )}
            />
            {!!errors.condition ? (
              <h6 className="mb-0 mt-0 mt-1 text-danger">
                {errors.condition?.message}
              </h6>
            ) : null}
            <Form.Text className="text-muted">
              Sélectionnez la condition pour ce template
            </Form.Text>
          </Form.Group>
        </Col>
        <Col lg={12}>
          <Form.Group className="mb-3">
            <Form.Label>Organisme :</Form.Label>
            <Controller
              control={control}
              name="organisation"
              render={({ field }) => (
                <SelectPaginateAsync
                  loadOptions={loadOrganisationsOptions}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.id.toString()}
                  value={field.value}
                  isClearable
                  onChange={field.onChange}
                />
              )}
            />
            <Form.Text className="text-muted">
              Associez ce template à un organisme (optionnel)
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>
    </Modal>
  );
};

export default forwardRef(StoreEmailTemplateModal);
